import { useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';
import toast from 'react-hot-toast';
import { namespaces, useSharedTranslation } from 'us-react-components/dist/hooks/useSharedTranslation';
import { useNavigate, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons/faCircleInfo';

import { Booking, BookingService, CompanyEmployee, UserVoucherService } from 'us-web-services';
import { Modal } from 'us-react-components/dist/Modal/Modal';
import { getDurationHours, getShortDurationString, getTimeOfDayString, getWeekdayDateString } from 'us-react-components/dist/utils/date';
import { fullName, sentenceCase } from 'us-react-components/dist/utils/string';

import { Button, ButtonType } from '../../components';
import { useDependencies } from '../../dependencies';

const DEFAULT_POLICY_URL =
  'https://urbansitter.zendesk.com/hc/en-us/sections/200296826-Corporate-Benefits';

const DEFAULT_AVATAR =
  'https://assets.urbansitter.com/static/img/us-default-avatar-320.png';
const CAREGIVER_TYPE_AGENCY = 2;

interface AvailableVoucher {
  userVoucherId: number;
  voucherCode?: string;
  usedValue: number;
}

interface BookingWithExtras extends Booking {
  originalStart?: string;
  originalEnd?: string;
  createdByRole?: 'parent' | 'sitter';
  message?: string;
  availableCredit?: number;
  availableVouchers?: AvailableVoucher[];
  order?: { employerCredit?: number };
  companyEmployee?: { company?: { policyUrl?: string } };
}

const formatCurrency = (n: number): string => n.toFixed(2);

export const BookingConfirmPayment = observer((): JSX.Element => {
  const { t } = useSharedTranslation(namespaces.family);
  const navigate = useNavigate();
  const { id: pathId } = useParams<{ id: string }>();
  const { stores, services: { tracker } } = useDependencies();
  const user = stores.user.user;

  const [booking, setBooking] = useState<BookingWithExtras | null>(null);
  const [tip, setTip] = useState<string>('');
  const [message, setMessage] = useState<string>('');
  const [usingBookedHours, setUsingBookedHours] = useState<boolean>(false);
  const [isConfirming, setIsConfirming] = useState<boolean>(false);
  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
  const [voucherToRemove, setVoucherToRemove] = useState<number | null>(null);
  const [removingVoucher, setRemovingVoucher] = useState(false);
  const [policyCertified, setPolicyCertified] = useState<boolean>(false);

  useEffect(() => {
    document.title = 'UrbanSitter - Confirm Payment';
  }, []);

  useEffect(() => {
    const bookingId = Number(pathId);
    if (!bookingId || !user?.id) return;
    const load = async (): Promise<void> => {
      try {
        const resp = await BookingService.get(bookingId, {
          params: { userId: user.id, fields: 'availableCredit,availableVouchers' },
        });
        const b = (resp.data?.data ?? null) as BookingWithExtras | null;
        if (!b) return;
        if (b.status?.code !== 'sitter_confirmed') {
          const msg =
            b.status?.code === 'job_finished'
              ? t('pages.bookingConfirmPayment.cannotConfirmYet')
              : t('pages.bookingConfirmPayment.alreadyConfirmed');
          toast.error(msg);
          setTimeout(() => navigate(`/bookings/${bookingId}`, { replace: true }), 1500);
          return;
        }
        setBooking(b);
        setMessage(b.message ?? '');
        setTip(b.tip && b.tip > 0 ? String(b.tip) : '');
      } catch (err) {
        console.warn('[BookingConfirmPayment] load failed', err);
      }
    };
    void load();
  }, [pathId, user?.id, navigate]);

  const originalDuration = useMemo(
    () => getDurationHours(booking?.originalStart, booking?.originalEnd),
    [booking?.originalStart, booking?.originalEnd],
  );
  const confirmedDuration = useMemo(
    () => getDurationHours(booking?.start, booking?.end),
    [booking?.start, booking?.end],
  );
  const isFlatRate = useMemo(() => booking?.paymentType === 'flat', [booking?.paymentType]);
  const isHourly = useMemo(() => !isFlatRate, [isFlatRate]);
  const isAgency = booking?.caregiverType === CAREGIVER_TYPE_AGENCY;
  const isInterview = booking?.type === 'interview';

  const effectiveDuration = usingBookedHours ? originalDuration : confirmedDuration;
  const rate = booking?.rate ?? 0;
  const jobRate = isFlatRate ? rate : rate * effectiveDuration;

  const tipNum = isAgency ? 0 : Number(tip) || 0;
  const subtotal = jobRate + tipNum;
  const vouchers = booking?.availableVouchers ?? [];
  const vouchersTotal = vouchers.reduce((acc, v) => acc + (v.usedValue ?? 0), 0);

  const parentRecord = stores.parent.parent as (typeof stores.parent.parent) & {
    companyEmployee?: CompanyEmployee;
  };
  const hasPlatformFeeWaived =
    !!parentRecord?.companyEmployee && parentRecord.companyEmployee.platformFeeWaived !== false;
  const platformFee =
    booking?.createdByRole === 'sitter' || hasPlatformFeeWaived
      ? 0
      : Math.ceil(jobRate * 0.03 * 100) / 100;
  const total = Math.max(0, subtotal + platformFee - vouchersTotal);

  const caregiverTotal = jobRate + tipNum;
  const creditApplied = Math.min(booking?.availableCredit ?? 0, total);

  const showPolicyCertification =
    (booking?.order?.employerCredit ?? 0) > 0 || !!parentRecord?.companyEmployee;
  const policyUrl = booking?.companyEmployee?.company?.policyUrl ?? DEFAULT_POLICY_URL;
  const submitBlockedByPolicy = showPolicyCertification && !policyCertified;

  const openRemoveVoucher = (userVoucherId: number): void => {
    setVoucherToRemove(userVoucherId);
  };

  const confirmRemoveVoucher = async (): Promise<void> => {
    if (!voucherToRemove || !booking?.id) return;
    setRemovingVoucher(true);
    try {
      await UserVoucherService.patch(voucherToRemove, {
        id: voucherToRemove,
        bookingId: null,
      } as never);
      setBooking((prev) =>
        prev
          ? {
              ...prev,
              availableVouchers: (prev.availableVouchers ?? []).filter(
                (v) => v.userVoucherId !== voucherToRemove,
              ),
            }
          : prev,
      );
      setVoucherToRemove(null);
      toast.success(t('pages.bookingConfirmPayment.discountRemoved'));
    } catch {
      toast.error(t('pages.bookingConfirmPayment.discountRemoveError'));
    } finally {
      setRemovingVoucher(false);
    }
  };

  const submit = async (skipModal = false): Promise<void> => {
    if (!booking?.id || !user?.id || isConfirming) return;
    if (!skipModal) {
      setShowConfirmModal(true);
      return;
    }

    setShowConfirmModal(false);
    setIsConfirming(true);
    try {
      const patchStart = usingBookedHours ? booking.originalStart : booking.start;
      const patchEnd = usingBookedHours ? booking.originalEnd : booking.end;
      await BookingService.patch(
        booking.id,
        {
          id: booking.id,
          start: patchStart,
          end: patchEnd,
          tip: tipNum,
          message,
          status: { code: 'parent_confirm_payment' },
        } as unknown as Booking,
        { params: { userId: user.id } },
      );
      navigate(`/bookings/${booking.id}`);
    } catch (err: unknown) {
      setIsConfirming(false);
      const anyErr = err as { response?: { data?: { errors?: Array<{ message?: string }> } } };
      const msgs = (anyErr?.response?.data?.errors ?? []).map((e) => e.message ?? '').filter(Boolean);
      toast.error(msgs.length ? msgs.join(' · ') : t('pages.bookingConfirmPayment.submitFailed'));
    }
  };

  if (!booking) {
    return (
      <div className='container py-8'>
        <h2>
          <i className='fa-solid fa-spinner fa-spin mr-2' />
          {t('common.loading')}
        </h2>
      </div>
    );
  }

  const caregiverFirstName = booking.caregiver?.firstName ?? '';
  const dateText = `${getWeekdayDateString(booking.originalStart ?? booking.start)}, ${getTimeOfDayString(booking.originalStart ?? booking.start)} - ${getTimeOfDayString(booking.originalEnd ?? booking.end)}`;
  const supportEmail = isAgency ? 'agencycare@urbansitter.com' : 'support@urbansitter.com';

  let subtitle: JSX.Element;
  if (confirmedDuration === originalDuration) {
    subtitle = (
      <p>
        {t('pages.bookingConfirmPayment.subtitleSame', {
          name: sentenceCase(caregiverFirstName),
          date: dateText,
        })}
      </p>
    );
  } else if (confirmedDuration > originalDuration) {
    subtitle = (
      <p>
        {t('pages.bookingConfirmPayment.subtitleMorePrefix', {
          name: sentenceCase(caregiverFirstName),
          date: dateText,
        })}{' '}
        <a href={`mailto:${supportEmail}`} className='underline'>
          {supportEmail}
        </a>
      </p>
    );
  } else {
    subtitle = (
      <p>
        {t('pages.bookingConfirmPayment.subtitleLess', {
          name: caregiverFirstName,
          date: dateText,
        })}
      </p>
    );
  }

  return (
    <div id='parent-confirm-details' className='container py-8'>
      <div className='mb-6'>
        <h1 className='text-2xl font-semibold mb-2'>
          {t('pages.bookingConfirmPayment.titleSame', { name: caregiverFirstName })}
        </h1>
        {subtitle}
      </div>

      <div className='grid grid-cols-1 md:grid-cols-12 gap-4'>
        <div className='md:col-span-5'>
          <div className='us-card rounded p-4 overflow-auto'>
            {!booking.caregiver?.active ? (
              <div className='float-left mr-4'>
                <img src={DEFAULT_AVATAR} className='w-28 h-28 rounded' alt='' />
              </div>
            ) : !isAgency ? (
              <a
                id={`booking-${booking.id}`}
                href={`/app/sitter/${booking.caregiver?.id}`}
                className='float-left mr-4'
              >
                <img
                  src={booking.caregiver?.pictureUrl}
                  alt={fullName(booking.caregiver)}
                  className='w-28 h-28 rounded'
                />
              </a>
            ) : (
              <span className='float-left mr-4'>
                <img
                  src={booking.caregiver?.pictureUrl}
                  alt={fullName(booking.caregiver)}
                  className='w-28 h-28 rounded'
                />
              </span>
            )}
            <div className='font-medium mt-3 mb-2'>{fullName(booking.caregiver)}</div>
            <div className='mb-2'>
              <b>{getShortDurationString(booking.originalStart, booking.originalEnd)}:</b>{' '}
              <span>{dateText}</span>
            </div>
          </div>
        </div>

        <div className='md:col-span-7'>
          <div className='us-card rounded p-5'>
            {confirmedDuration !== originalDuration && (
              <>
                <div className='flex items-center justify-between mb-3'>
                  <div>
                    <b>
                      {t('pages.bookingConfirmPayment.confirmedTime')}{' '}
                      <span className='text-emerald'>
                        {getShortDurationString(booking.start, booking.end)}
                      </span>
                    </b>
                    <div>
                      {getWeekdayDateString(booking.start)}, {getTimeOfDayString(booking.start)} -{' '}
                      {getTimeOfDayString(booking.end)}
                    </div>
                  </div>
                  <div>
                    {confirmedDuration < originalDuration && !isInterview && !usingBookedHours && (
                      <Button
                        buttonType={ButtonType.tertiary}
                        onClick={(): void => setUsingBookedHours(true)}
                      >
                        {t('pages.bookingConfirmPayment.useBookedHours')}
                      </Button>
                    )}
                    {usingBookedHours && (
                      <Button
                        buttonType={ButtonType.tertiary}
                        onClick={(): void => setUsingBookedHours(false)}
                      >
                        {t('pages.bookingConfirmPayment.useConfirmedHours')}
                      </Button>
                    )}
                  </div>
                </div>
                <hr className='my-3' />
              </>
            )}

            <div className='grid grid-cols-2 gap-4'>
              <div>
                <div className='mb-2 font-medium'>{t('pages.bookingConfirmPayment.startTime')}</div>
                <span>{getTimeOfDayString(booking.start)}</span>
              </div>
              <div>
                <div className='mb-2 font-medium'>{t('pages.bookingConfirmPayment.endTime')}</div>
                <span>{getTimeOfDayString(booking.end)}</span>
              </div>
            </div>
            <hr className='my-3' />

            <div className='grid grid-cols-2 gap-4 mb-2'>
              <div>
                {!isInterview ? (
                  <>
                    <span className='font-medium'>{t('pages.bookingConfirmPayment.service')}</span>
                    <br />
                    <span>
                      {getShortDurationString(
                        usingBookedHours ? booking.originalStart : booking.start,
                        usingBookedHours ? booking.originalEnd : booking.end,
                      )}{' '}
                      {isHourly
                        ? t('pages.bookingConfirmPayment.perHour', { rate })
                        : t('pages.bookingConfirmPayment.flatRate', { rate })}
                    </span>
                  </>
                ) : (
                  <b>{t('pages.bookingConfirmPayment.workingInterview')}</b>
                )}
              </div>
              <div id='job-rate' className='text-right'>
                ${formatCurrency(jobRate)}
              </div>
            </div>
            <hr className='my-3' />

            {platformFee > 0 && (
              <>
                <div className='grid grid-cols-2 gap-4 mb-2'>
                  <div>
                    <span className='font-medium'>{t('pages.bookingConfirmPayment.platformFee')}</span>
                    <FontAwesomeIcon
                      id='platform-fee-info'
                      icon={faCircleInfo}
                      className='ml-1 text-sm align-middle text-neutral-500'
                      title={t('pages.bookingConfirmPayment.platformFeeTip')}
                    />
                    <br />
                    <span className='font-medium'>{t('pages.bookingConfirmPayment.platformFeePercent')}</span>
                  </div>
                  <div id='platform-fee-amount' className='text-right'>
                    ${formatCurrency(platformFee)}
                  </div>
                </div>
                <hr className='my-3' />
              </>
            )}

            <div className='grid grid-cols-2 gap-4 mb-2'>
              <div>
                <span className='font-medium'>{t('pages.bookingConfirmPayment.subtotal')}</span>
              </div>
              <div id='subtotal-amount' className='text-right'>
                ${formatCurrency(subtotal)}
              </div>
            </div>
            {vouchers.length > 0 && (
              <div className='mt-2'>
                {vouchers.map((v) => (
                  <div key={v.userVoucherId} className='grid grid-cols-2 gap-4 mb-1'>
                    <div className='ml-4'>
                      <span>{v.voucherCode ?? `Voucher #${v.userVoucherId}`}</span>
                    </div>
                    <div className='text-right'>
                      <span>-${formatCurrency(v.usedValue ?? 0)}</span>
                      <Button
                        buttonType={ButtonType.tertiary}
                        onClick={(): void => openRemoveVoucher(v.userVoucherId)}
                        className='ml-3 text-neutral-500 hover:text-neutral-700'
                        aria-label={t('pages.bookingConfirmPayment.removeDiscountAriaLabel')}
                      >
                        <i className='fa-solid fa-times' />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <hr className='my-3' />

            {!isAgency && (
              <>
                <div className='grid grid-cols-12 gap-3'>
                  <div className='col-span-7'>
                    <span className='font-medium'>{t('pages.bookingConfirmPayment.addTip')}</span>
                  </div>
                  <div className='col-span-5 text-right'>
                    <div className='inline-flex items-center border border-neutral-300 rounded overflow-hidden'>
                      <div className='px-3 bg-neutral-100'>$</div>
                      <input
                        id='tip'
                        className='form-control text-right w-20 px-2 py-1'
                        type='number'
                        min='0'
                        maxLength={4}
                        step='1'
                        value={tip}
                        onChange={(e): void => setTip(e.target.value.replace(/[^0-9]/g, ''))}
                        placeholder='0'
                      />
                    </div>
                  </div>
                </div>
                <hr className='my-3' />
              </>
            )}

            <div className='grid grid-cols-2 gap-4 mb-8'>
              <div>
                <span className='font-medium'>{t('pages.bookingConfirmPayment.total')}</span>
              </div>
              <span id='total-charge-amount' className='text-right font-bold'>
                ${formatCurrency(total)}
              </span>
            </div>

            {!isAgency && (
              <div className='mb-4'>
                <h4 className='font-medium'>
                  {t('pages.bookingConfirmPayment.messageTo', { name: caregiverFirstName })}
                </h4>
                <textarea
                  id='message'
                  maxLength={255}
                  rows={3}
                  placeholder={t('pages.bookingConfirmPayment.messagePlaceholder', {
                    name: caregiverFirstName,
                  })}
                  value={message}
                  onChange={(e): void => setMessage(e.target.value)}
                  className='form-control w-full border border-neutral-300 rounded p-2'
                />
              </div>
            )}

            {showPolicyCertification && (
              <div id='policy-certification' className='us-card rounded border border-neutral-200 p-4 mt-4 mb-4'>
                <label
                  htmlFor='policy-certification-checkbox'
                  className='flex items-start gap-2 cursor-pointer'
                >
                  <input
                    id='policy-certification-checkbox'
                    type='checkbox'
                    className='mt-1'
                    checked={policyCertified}
                    onChange={(e): void => {
                      setPolicyCertified(e.target.checked);
                      // Angular parent-confirm.js:254 — fires only on check (not uncheck).
                      if (e.target.checked) {
                        tracker.track('clicked-button', {
                          action: 'policy-checkbox-checked',
                          elementId: 'policy-certification-checkbox',
                          elementName: 'Policy Certification Checkbox',
                          category: 'policy-certification',
                          component: 'parent-confirm-payment',
                          bookingId: booking?.id,
                        });
                      }
                    }}
                  />
                  <span>
                    I certify that to my knowledge, this booking was for qualified services from a care provider that meets the guidelines communicated to me by my employer.{' '}
                    <a
                      id='policy-link'
                      href={policyUrl}
                      target='_blank'
                      rel='noopener noreferrer'
                      className='underline'
                    >
                      View employer policy
                    </a>
                  </span>
                </label>
              </div>
            )}

            <div className='text-right'>
              <Button
                id='continue-cta'
                buttonType={ButtonType.primary}
                onClick={(): Promise<void> => submit(false)}
                disabled={isConfirming || submitBlockedByPolicy}
                className='px-10'
              >
                {isConfirming ? (
                  <>
                    <i className='fa-solid fa-spinner fa-spin mr-2' />
                    {t('pages.bookingConfirmPayment.submitting')}
                  </>
                ) : (
                  t('pages.bookingConfirmPayment.payNow')
                )}
              </Button>
            </div>

            <small className='block mt-5 text-xs'>
              <p className='mb-1'>{t('pages.bookingConfirmPayment.doubleCheck')}</p>
              <p className='mb-1'>{t('pages.bookingConfirmPayment.cardWillCharge')}</p>
              <p>
                {t('pages.bookingConfirmPayment.disagreeContact')}{' '}
                <a href={`mailto:${supportEmail}`} className='underline'>
                  {supportEmail}
                </a>
              </p>
            </small>
          </div>
        </div>
      </div>

      <Modal
        isOpen={showConfirmModal}
        onClose={(): void => setShowConfirmModal(false)}
        title={t('pages.bookingConfirmPayment.confirmModalTitle')}
        className='w-full max-w-md'
        footer={
          <>
            <Button
              id='confirm-modal-cta'
              buttonType={ButtonType.primary}
              className='flex-1'
              disabled={isConfirming}
              onClick={(): Promise<void> => submit(true)}
            >
              {t('pages.bookingConfirmPayment.yesConfirm')}
            </Button>
            {!isConfirming && (
              <Button
                id='cancel-modal-cta'
                buttonType={ButtonType.secondary}
                className='flex-1'
                onClick={(): void => setShowConfirmModal(false)}
              >
                {t('common.cancel')}
              </Button>
            )}
          </>
        }
      >
        {!isAgency && (
          <p className='font-bold'>
            {t('pages.bookingConfirmPayment.caregiverWillReceive', {
              name: fullName(booking.caregiver),
              amount: formatCurrency(caregiverTotal),
            })}
          </p>
        )}
        {!!booking.availableCredit && (
          <p className='text-neutral-600 mt-2'>
            {t('pages.bookingConfirmPayment.creditApplied', {
              amount: formatCurrency(creditApplied),
            })}
          </p>
        )}
        <p className='font-bold mt-2'>
          {t('pages.bookingConfirmPayment.cardWillBeCharged', {
            amount: formatCurrency(total),
          })}
        </p>
      </Modal>

      <Modal
        isOpen={voucherToRemove != null}
        onClose={(): void => setVoucherToRemove(null)}
        title={t('pages.bookingConfirmPayment.removeVoucherTitle')}
        className='w-full max-w-md'
        footer={
          <>
            <Button
              buttonType={ButtonType.tertiary}
              onClick={(): void => setVoucherToRemove(null)}
            >
              {t('common.cancel')}
            </Button>
            <Button
              id='confirm-remove-voucher'
              buttonType={ButtonType.primary}
              disabled={removingVoucher}
              onClick={confirmRemoveVoucher}
            >
              {t('pages.bookingConfirmPayment.removeVoucherConfirm')}
            </Button>
          </>
        }
      >
        <p>{t('pages.bookingConfirmPayment.removeVoucherBody')}</p>
      </Modal>
    </div>
  );
});
